/**
 * Trading-relevant national flags (V2.5.2). Emoji-only (no asset cost; OS-rendered).
 * Each entry: { code: ISO-3166-1 alpha-2, name: country, flag: emoji }.
 * Ordered: US first, then alphabetical for the dropdown.
 */
export const FLAGS = [
  { code: "US", name: "United States",    flag: "🇺🇸" },
  // — alphabetical from here —
  { code: "AR", name: "Argentina",        flag: "🇦🇷" },
  { code: "AU", name: "Australia",        flag: "🇦🇺" },
  { code: "AT", name: "Austria",          flag: "🇦🇹" },
  { code: "BE", name: "Belgium",          flag: "🇧🇪" },
  { code: "BR", name: "Brazil",           flag: "🇧🇷" },
  { code: "CA", name: "Canada",           flag: "🇨🇦" },
  { code: "CL", name: "Chile",            flag: "🇨🇱" },
  { code: "CN", name: "China",            flag: "🇨🇳" },
  { code: "CO", name: "Colombia",         flag: "🇨🇴" },
  { code: "CZ", name: "Czech Republic",   flag: "🇨🇿" },
  { code: "DK", name: "Denmark",          flag: "🇩🇰" },
  { code: "EG", name: "Egypt",            flag: "🇪🇬" },
  { code: "FI", name: "Finland",          flag: "🇫🇮" },
  { code: "FR", name: "France",           flag: "🇫🇷" },
  { code: "DE", name: "Germany",          flag: "🇩🇪" },
  { code: "GR", name: "Greece",           flag: "🇬🇷" },
  { code: "HK", name: "Hong Kong",        flag: "🇭🇰" },
  { code: "HU", name: "Hungary",          flag: "🇭🇺" },
  { code: "IN", name: "India",            flag: "🇮🇳" },
  { code: "ID", name: "Indonesia",        flag: "🇮🇩" },
  { code: "IE", name: "Ireland",          flag: "🇮🇪" },
  { code: "IL", name: "Israel",           flag: "🇮🇱" },
  { code: "IT", name: "Italy",            flag: "🇮🇹" },
  { code: "JP", name: "Japan",            flag: "🇯🇵" },
  { code: "MY", name: "Malaysia",         flag: "🇲🇾" },
  { code: "MX", name: "Mexico",           flag: "🇲🇽" },
  { code: "NL", name: "Netherlands",      flag: "🇳🇱" },
  { code: "NZ", name: "New Zealand",      flag: "🇳🇿" },
  { code: "NG", name: "Nigeria",          flag: "🇳🇬" },
  { code: "NO", name: "Norway",           flag: "🇳🇴" },
  { code: "PE", name: "Peru",             flag: "🇵🇪" },
  { code: "PH", name: "Philippines",      flag: "🇵🇭" },
  { code: "PL", name: "Poland",           flag: "🇵🇱" },
  { code: "PT", name: "Portugal",         flag: "🇵🇹" },
  { code: "RO", name: "Romania",          flag: "🇷🇴" },
  { code: "RU", name: "Russia",           flag: "🇷🇺" },
  { code: "SA", name: "Saudi Arabia",     flag: "🇸🇦" },
  { code: "SG", name: "Singapore",        flag: "🇸🇬" },
  { code: "ZA", name: "South Africa",     flag: "🇿🇦" },
  { code: "KR", name: "South Korea",      flag: "🇰🇷" },
  { code: "ES", name: "Spain",            flag: "🇪🇸" },
  { code: "SE", name: "Sweden",           flag: "🇸🇪" },
  { code: "CH", name: "Switzerland",      flag: "🇨🇭" },
  { code: "TW", name: "Taiwan",           flag: "🇹🇼" },
  { code: "TH", name: "Thailand",         flag: "🇹🇭" },
  { code: "TR", name: "Türkiye",          flag: "🇹🇷" },
  { code: "UA", name: "Ukraine",          flag: "🇺🇦" },
  { code: "AE", name: "UAE",              flag: "🇦🇪" },
  { code: "GB", name: "United Kingdom",   flag: "🇬🇧" },
  { code: "VN", name: "Vietnam",          flag: "🇻🇳" },
];

const _byCode = Object.fromEntries(FLAGS.map(f => [f.code, f]));

export function flagFor(code) {
  if (!code) return null;
  return _byCode[String(code).toUpperCase()] || null;
}

/**
 * <FlagBadge code="US" /> — tiny inline emoji + ISO code for nameplates.
 * Returns null if no code is set, so it cleanly disappears for users who
 * haven't picked a country yet.
 */
export function FlagBadge({ code, showCode = false, className = "", size = "sm" }) {
  const f = flagFor(code);
  if (!f) return null;
  const px = size === "lg" ? "text-base" : size === "md" ? "text-sm" : "text-[12px]";
  return (
    <span data-testid={`flag-${f.code}`} title={f.name} className={`inline-flex items-center gap-1 ${px} ${className}`}>
      <span>{f.flag}</span>
      {showCode && <span className="font-mono text-[10px] text-slate-400">{f.code}</span>}
    </span>
  );
}
